import * as THREE from 'three';
import { BallState } from './BallState';
import type { TerrainSurfaceProbe } from './TerrainSurfaceProbe';
import type { WindField } from './WindField';
import { PhysicsEngine } from './PhysicsEngine';

export class GolfBall {
  readonly state: BallState;
  readonly object3D: THREE.Object3D;

  private readonly mesh: THREE.Mesh;
  private readonly shadowDisk: THREE.Mesh;
  private readonly physics = new PhysicsEngine();
  private dropAnimation: {
    elapsed: number;
    duration: number;
    start: THREE.Vector3;
    cup: THREE.Vector3;
  } | null = null;

  constructor(initialPosition: THREE.Vector3, markerColor?: number) {
    this.state = new BallState(initialPosition);
    // The simulation radius remains a real golf-ball radius in CollisionResolver.
    // The visible mesh is slightly enlarged for the first prototype so the
    // behind-ball camera can reliably track it before we add zoom/DOF/presentation polish.
    const geometry = new THREE.SphereGeometry(0.055, 32, 20);
    const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.28 });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = 'GolfBallMesh';
    this.mesh.castShadow = true;

    const shadowGeometry = new THREE.CircleGeometry(0.11, 32);
    const shadowMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    });
    this.shadowDisk = new THREE.Mesh(shadowGeometry, shadowMaterial);
    this.shadowDisk.name = 'GolfBallContactShadow';
    this.shadowDisk.rotation.x = -Math.PI / 2;
    this.shadowDisk.position.y = -0.0205;

    const group = new THREE.Group();
    group.name = 'GolfBall';
    group.add(this.mesh);
    if (markerColor !== undefined) {
      const marker = new THREE.Mesh(
        new THREE.RingGeometry(0.075, 0.095, 32),
        new THREE.MeshBasicMaterial({
          color: markerColor,
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
        }),
      );
      marker.name = 'GolfBallPlayerIndicator';
      marker.rotation.x = -Math.PI / 2;
      marker.position.y = 0.006;
      group.add(marker);
    }
    group.add(this.shadowDisk);
    this.object3D = group;
    this.syncObject();
  }

  launch(
    velocity: THREE.Vector3,
    spin = new THREE.Vector3(0, 0, 0),
    options: {
      readonly rolloutResistanceMultiplier?: number;
      readonly bounceRestitutionMultiplier?: number;
    } = {},
  ): void {
    this.state.setLaunch(velocity, spin, options);
  }

  putt(velocity: THREE.Vector3, spin = new THREE.Vector3(0, 0, 0)): void {
    this.state.setRolling(velocity, spin, 'putt');
  }

  reset(position: THREE.Vector3): void {
    this.dropAnimation = null;
    this.mesh.visible = true;
    this.shadowDisk.visible = true;
    this.state.reset(position);
    this.syncObject();
  }

  captureIntoCup(cupPosition: THREE.Vector3): void {
    this.dropAnimation = {
      elapsed: 0,
      duration: 0.82,
      start: this.state.position.clone(),
      cup: cupPosition.clone(),
    };
    this.state.velocity.set(0, 0, 0);
    this.state.spin.set(0, 0, 0);
    this.state.isResting = true;
    this.state.phase = 'resting';
    this.state.motionMode = 'resting';
  }

  isCupDropComplete(): boolean {
    return this.dropAnimation !== null && this.dropAnimation.elapsed >= this.dropAnimation.duration;
  }

  update(
    probe: TerrainSurfaceProbe,
    wind: WindField,
    deltaSeconds: number,
    elapsedSeconds: number,
  ): void {
    if (this.dropAnimation) {
      this.updateCupDrop(deltaSeconds);
      return;
    }
    this.physics.step(this.state, probe, wind, deltaSeconds, elapsedSeconds);
    this.syncObject();
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.shadowDisk.geometry.dispose();
    const materials = [this.mesh.material, this.shadowDisk.material].flat();
    materials.forEach((material) => material.dispose());
  }

  private updateCupDrop(deltaSeconds: number): void {
    if (!this.dropAnimation) return;
    const animation = this.dropAnimation;
    animation.elapsed = Math.min(animation.duration, animation.elapsed + deltaSeconds);
    const t = animation.elapsed / animation.duration;
    const rollT = THREE.MathUtils.smoothstep(Math.min(t / 0.42, 1), 0, 1);
    const dropT = THREE.MathUtils.smoothstep(THREE.MathUtils.clamp((t - 0.35) / 0.65, 0, 1), 0, 1);
    this.object3D.position.lerpVectors(animation.start, animation.cup, rollT);
    this.object3D.position.y = THREE.MathUtils.lerp(
      animation.start.y,
      animation.cup.y - 0.16,
      dropT,
    );
    const visibleScale = THREE.MathUtils.lerp(1, 0.35, dropT);
    this.mesh.scale.setScalar(visibleScale);
    this.shadowDisk.scale.setScalar(THREE.MathUtils.lerp(1, 0.15, dropT));
    this.shadowDisk.visible = t < 0.72;
    if (animation.elapsed >= animation.duration) {
      this.mesh.visible = false;
      this.shadowDisk.visible = false;
      this.state.position.copy(animation.cup).setY(animation.cup.y - 0.16);
    }
  }

  private syncObject(): void {
    this.object3D.position.copy(this.state.position);
    this.mesh.scale.setScalar(1);
    this.shadowDisk.scale.setScalar(1);
  }
}
